
import { Component, Vue, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import SmsRepository from '@/api/repositories/SmsRepository';
import debounce, { DebouncedFunction } from '@/misc/debounce';

interface SmsStats {
    total: number,
    succeeded: number,
    failed: number,
    successRate: number | null
}

interface AccountStatus {
    ok: boolean,
    balance?: number,
    currency?: string
}

const smsRepository: SmsRepository = RepositoryFactory.get('sms');
// Warn when the prepaid balance drops below this (provider currency).
const LOW_BALANCE_THRESHOLD = 5;

// How long typing in the phone filter has to settle before the logs reload.
const FILTER_DEBOUNCE_MS = 400;

// Shortest partial phone number worth sending to the server. An empty field is
// exempt: it means "no phone filter" and should reload the unfiltered list.
const MIN_AUTO_FILTER_LENGTH = 3;

@Component({
    components: {
        SupportLoginCodeComponent: () => import(
            /* webpackChunkName: "SupportLoginCodeComponent" */
            '@/components/SupportLoginCode.component.vue'
        )
    }
})
export default class SmsMonitorView extends Vue {
    public isLoadingLogs: boolean = false;
    public isLoadingStats: boolean = false;
    public isLoadingAccount: boolean = false;

    public items: any[] = [];
    public total: number = 0;
    public options: any = { page: 1, itemsPerPage: 25, sortBy: [], sortDesc: [] };

    public stats: SmsStats = { total: 0, succeeded: 0, failed: 0, successRate: null };
    public account: AccountStatus = { ok: false };

    // Tracks the in-flight per-row action (WhatsApp/resend); null when idle.
    public actingKey: string | null = null;

    public filters: { status: string | null, purpose: string | null, phone: string | null } = {
        status: null,
        purpose: null,
        phone: null
    };

    /**
     * Identifies the most recent log request. Typing can leave several in
     * flight and they need not resolve in order, so a response is discarded
     * unless its token is still current.
     */
    private logsToken: number = 0;

    /**
     * Set when a reload was triggered by typing rather than a deliberate
     * action, and consumed by the next `loadLogs`. Kept as state rather than a
     * parameter because the reload can reach `loadLogs` indirectly, via the
     * `options` watcher, when the page has to be reset first.
     */
    private autoTriggeredLoad: boolean = false;

    private debouncedPhoneFilter: DebouncedFunction = debounce(() => this.onFilterChange(true), FILTER_DEBOUNCE_MS);

    /**
     * Set by the clear button so the `input` event Vuetify raises a tick later
     * does not schedule a second, redundant reload. See `onPhoneFilterCleared`.
     */
    private ignoreNextPhoneInput: boolean = false;

    public statusOptions = ['QUEUED', 'SENT', 'FAILED', 'DELIVERED', 'UNDELIVERED'];
    public purposeOptions = [
        { text: 'OTP – Customer', value: 'OTP_CUSTOMER' },
        { text: 'OTP – Driver', value: 'OTP_DRIVER' },
        { text: 'Other', value: 'OTHER' }
    ];

    public get headers() {
        return [
            { text: this.$t('SMS_MONITOR.TABLE.TIME'), value: 'createdAt', sortable: false },
            { text: this.$t('SMS_MONITOR.TABLE.RECIPIENT'), value: 'recipient', sortable: false },
            { text: this.$t('SMS_MONITOR.TABLE.PURPOSE'), value: 'purpose', sortable: false },
            { text: this.$t('SMS_MONITOR.TABLE.STATUS'), value: 'status', sortable: false },
            { text: this.$t('SMS_MONITOR.TABLE.NETWORK'), value: 'network', sortable: false },
            { text: this.$t('SMS_MONITOR.TABLE.PRICE'), value: 'price', sortable: false },
            { text: this.$t('SMS_MONITOR.TABLE.ERROR'), value: 'errorText', sortable: false },
            { text: this.$t('SMS_MONITOR.TABLE.ACTIONS'), value: 'actions', sortable: false }
        ];
    }

    public get isLoading(): boolean {
        return this.isLoadingLogs || this.isLoadingStats || this.isLoadingAccount;
    }

    public get isLowBalance(): boolean {
        return this.account.ok && this.account.balance !== undefined && this.account.balance < LOW_BALANCE_THRESHOLD;
    }

    async created() {
        await this.refreshAll();
    }

    @Watch('options')
    private onOptionsChange() {
        this.loadLogs();
    }

    public async refreshAll() {
        await Promise.all([
            this.loadLogs(),
            this.loadStats(),
            this.loadAccount()
        ]);
    }

    /**
     * Runs on every keystroke in the phone filter. An empty field is a real
     * state — it means "no phone filter" — so it reloads. One or two
     * characters is a half-typed number: skip it rather than filtering the
     * table down to noise on the way to a usable query.
     */
    public onPhoneFilterInput() {
        // The clear button already reloaded; this is just Vuetify catching the
        // model up a tick later.
        if (this.ignoreNextPhoneInput) {
            this.ignoreNextPhoneInput = false;
            return;
        }

        const phone = (this.filters.phone ?? '').trim();
        if (phone.length > 0 && phone.length < MIN_AUTO_FILTER_LENGTH) {
            this.debouncedPhoneFilter.cancel();
            return;
        }
        this.debouncedPhoneFilter();
    }

    /**
     * The clear (X) button is a deliberate action, so it reloads at once
     * rather than waiting out the typing debounce.
     *
     * Two Vuetify details make the obvious wiring wrong (verified against
     * 2.6.13). `click:clear` is emitted synchronously *before* the model is
     * touched — `clearableCallback` defers `internalValue = null` to the next
     * tick — so reloading straight from this handler would re-query with the
     * old phone filter still set. And that deferred assignment emits `input`
     * unconditionally, which would then schedule a second reload.
     *
     * Clearing the model here rather than waiting for Vuetify sidesteps both,
     * and keeps this independent of tick ordering.
     */
    public onPhoneFilterCleared() {
        this.debouncedPhoneFilter.cancel();
        this.ignoreNextPhoneInput = true;
        this.filters.phone = null;
        this.onFilterChange();
    }

    /**
     * @param auto whether typing triggered this rather than a deliberate
     * action. Template call sites must keep the explicit `onFilterChange()`
     * parentheses — a bare handler reference hands Vue's event (or, on the
     * selects, the chosen value) in as `auto`, which is truthy.
     */
    public onFilterChange(auto: boolean = false) {
        this.autoTriggeredLoad = auto;
        // Reset to first page whenever a filter changes, then reload.
        if (this.options.page !== 1) {
            this.options = { ...this.options, page: 1 };
        } else {
            this.loadLogs();
        }
    }

    private async loadLogs() {
        const auto = this.autoTriggeredLoad;
        this.autoTriggeredLoad = false;
        const token = ++this.logsToken;
        try {
            this.isLoadingLogs = true;
            const response = await smsRepository.getLogs({
                page: this.options.page,
                pageSize: this.options.itemsPerPage,
                status: this.filters.status || undefined,
                purpose: this.filters.purpose || undefined,
                phone: this.filters.phone || undefined
            });
            if (token !== this.logsToken) return;
            this.items = response.data.items ?? [];
            this.total = response.data.total ?? 0;
        } catch (e) {
            if (token !== this.logsToken) return;
            // Half-typed filters fail routinely; only surface it when the user
            // actually asked for this load.
            if (!auto) Vue.notifyErrorSimplified('SMS_MONITOR.ERROR_LOGS');
        } finally {
            if (token === this.logsToken) this.isLoadingLogs = false;
        }
    }

    private async loadStats() {
        try {
            this.isLoadingStats = true;
            const response = await smsRepository.getStats();
            this.stats = {
                total: response.data.total ?? 0,
                succeeded: response.data.succeeded ?? 0,
                failed: response.data.failed ?? 0,
                successRate: response.data.successRate ?? null
            };
        } catch (e) {
            // Non-fatal: cards just stay at defaults.
        } finally {
            this.isLoadingStats = false;
        }
    }

    private async loadAccount() {
        try {
            this.isLoadingAccount = true;
            const response = await smsRepository.getAccountStatus();
            this.account = response.data ?? { ok: false };
        } catch (e) {
            this.account = { ok: false };
        } finally {
            this.isLoadingAccount = false;
        }
    }

    public formatDate(value: string): string {
        return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '';
    }

    public statusColor(status: string): string {
        switch (status) {
            case 'DELIVERED': return 'green';
            case 'SENT': return 'blue';
            case 'FAILED':
            case 'UNDELIVERED': return 'red';
            default: return 'grey';
        }
    }

    // Unique key per row+action so only the clicked button shows a spinner.
    public rowKey(item: any, kind: string): string {
        const id = item && (item.id || item._id || `${item.recipient}-${item.createdAt}`);
        return `${id}-${kind}`;
    }

    // A row can be acted on only if it carries a structured phone.
    public canAct(item: any): boolean {
        return !!(item && item.phone && item.phone.prefix && item.phone.phoneNumber);
    }

    private typeFromPurpose(purpose: string): string {
        return purpose === 'OTP_DRIVER' ? 'DRIVER' : 'CUSTOMER';
    }

    public async sendWhatsappOtp(item: any) {
        if (!this.canAct(item) || this.actingKey !== null) {
            return;
        }
        try {
            this.actingKey = this.rowKey(item, 'whatsapp');
            const response = await smsRepository.sendWhatsapp(item.phone, this.typeFromPurpose(item.purpose));
            if (response && response.data && response.data.ok === false) {
                Vue.notifyErrorSimplified('SMS_MONITOR.ACTIONS.WHATSAPP_ERROR');
            } else {
                Vue.notifySuccessSimplified('SMS_MONITOR.ACTIONS.WHATSAPP_SUCCESS');
            }
            await this.loadLogs();
        } catch (e) {
            Vue.notifyErrorSimplified('SMS_MONITOR.ACTIONS.WHATSAPP_ERROR');
        } finally {
            this.actingKey = null;
        }
    }

    public async resendSms(item: any) {
        if (!this.canAct(item) || this.actingKey !== null) {
            return;
        }
        try {
            this.actingKey = this.rowKey(item, 'resend');
            await smsRepository.resend(item.phone, this.typeFromPurpose(item.purpose));
            Vue.notifySuccessSimplified('SMS_MONITOR.ACTIONS.RESEND_SUCCESS');
            await this.loadLogs();
        } catch (e) {
            Vue.notifyErrorSimplified('SMS_MONITOR.ACTIONS.RESEND_ERROR');
        } finally {
            this.actingKey = null;
        }
    }
}
